/* The WhatsApp chooser - personal or business, asked once per tap.

   Tuttu, 17 October: "When they click on the WhatsApp buttons anywhere, it
   should ask if they can text using the personal WhatsApp or business
   WhatsApp and should launch the selected mode. This is important as many
   people faced this problem and will be hard to scale it otherwise."

   THE PROBLEM. A wa.me link is a web address. The phone sees https, hands
   it to whichever app has claimed WhatsApp's domains, and the visitor has
   no say. On a phone with both apps installed that is almost always the
   wrong one, and there is no setting on the website that can change it,
   because the choice is made by the operating system after the link has
   already left the page.

   WHAT CAN ACTUALLY BE DONE, HONESTLY, PER PLATFORM:

     Android  - solved. An intent:// URL can name the exact package, and
                Android will open that package and no other:
                  com.whatsapp      personal
                  com.whatsapp.w4b  business
                Both buttons work, and a browser_fallback_url sends anyone
                without the app to wa.me instead of a dead end.

     iOS      - not solvable from a web page, and no amount of code changes
                that. Both apps register the same whatsapp:// scheme and iOS
                resolves it to one of them without asking. So the sheet does
                not pretend: iPhone gets one app button and the web option,
                with a line saying why.

     Desktop  - offers the desktop app and web.whatsapp.com, which is a real
                choice and worth having.

   The sheet is deliberately plain. It appears, it is answered, it is gone -
   anything slower would be worse than the problem it fixes.

   EVERY COLOUR IN gs-whatsapp.css IS A LITERAL. This element is appended to
   <body>, outside .gs-team, where the --t-* tokens are not declared. Three
   bugs in this project have been exactly that mistake. */

(function () {
  'use strict';

  var KEY = 'gs-wa-app';
  var box = null;

  /* ------------------------------------------------------------ platform */

  var UA = navigator.userAgent || '';
  var IOS = /iPad|iPhone|iPod/.test(UA) ||
            (/Macintosh/.test(UA) && navigator.maxTouchPoints > 1);
  var ANDROID = /Android/.test(UA);
  var PHONE = IOS || ANDROID;

  /* --------------------------------------------------- reading the link */

  /* Every shape a WhatsApp link takes on this site, reduced to a number and
     a message. wa.me puts the number in the path; api.whatsapp.com and the
     whatsapp: scheme put it in the query. */
  function parse(href) {
    var phone = '';
    var text = '';
    var u;

    try {
      u = new URL(href, window.location.href);
    } catch (err) {
      return null;
    }

    var host = (u.hostname || '').toLowerCase();
    var scheme = (u.protocol || '').toLowerCase();

    if (scheme === 'whatsapp:') {
      /* whatsapp://send?phone=... - the URL parser leaves the host empty
         and puts "send" in the pathname, so read the query directly. */
      var q = new URLSearchParams((href.split('?')[1] || ''));
      phone = q.get('phone') || '';
      text = q.get('text') || '';
    } else if (host === 'wa.me' || host === 'www.wa.me') {
      phone = (u.pathname || '').replace(/[^0-9]/g, '');
      text = u.searchParams.get('text') || '';
    } else if (host.indexOf('whatsapp.com') >= 0) {
      phone = u.searchParams.get('phone') || '';
      text = u.searchParams.get('text') || '';
    } else {
      return null;
    }

    phone = String(phone).replace(/[^0-9]/g, '');
    if (!phone) return null;
    return { phone: phone, text: text };
  }

  /* --------------------------------------------------- the four targets */

  function webUrl(d) {
    return 'https://wa.me/' + d.phone + (d.text ? '?text=' + encodeURIComponent(d.text) : '');
  }

  function schemeUrl(d) {
    return 'whatsapp://send?phone=' + d.phone +
      (d.text ? '&text=' + encodeURIComponent(d.text) : '');
  }

  function androidUrl(d, pkg) {
    return 'intent://send?phone=' + d.phone +
      (d.text ? '&text=' + encodeURIComponent(d.text) : '') +
      '#Intent;scheme=whatsapp;package=' + pkg +
      ';S.browser_fallback_url=' + encodeURIComponent(webUrl(d)) + ';end';
  }

  function go(d, which) {
    if (which === 'web') { window.open(webUrl(d), '_blank', 'noopener'); return; }

    if (ANDROID) {
      window.location.href = androidUrl(
        d, which === 'business' ? 'com.whatsapp.w4b' : 'com.whatsapp'
      );
      return;
    }

    /* iPhone and desktop: the scheme is all there is. On desktop a visitor
       without the app sees nothing happen, so wa.me follows as a safety net
       a beat later - harmless if the app did open, because the tab is then
       already in the background. */
    window.location.href = schemeUrl(d);
    if (!PHONE) {
      window.setTimeout(function () {
        window.open(webUrl(d), '_blank', 'noopener');
      }, 1400);
    }
  }

  /* ---------------------------------------------------------- the sheet */

  var MARK = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.22 8.22 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.82c0 4.54-3.7 8.23-8.24 8.23Zm4.52-6.16c-.25-.13-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.15.16-.29.18-.54.06-.25-.13-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.44.13-.15.17-.25.25-.42.08-.16.04-.31-.02-.44-.06-.12-.56-1.35-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.16 0-.43.06-.65.31-.23.25-.86.84-.86 2.05s.88 2.38 1 2.54c.12.17 1.73 2.64 4.19 3.7.59.26 1.04.4 1.4.52.59.19 1.12.16 1.54.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.29Z"/></svg>';

  function shut() {
    if (!box) return;
    box.hidden = true;
    box.innerHTML = '';
  }

  function shell() {
    if (box) return;
    box = document.createElement('div');
    box.className = 'gs-wa';
    box.hidden = true;
    document.body.appendChild(box);

    box.addEventListener('click', function (e) {
      if (e.target === box || (e.target.closest && e.target.closest('[data-wa-shut]'))) shut();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && box && !box.hidden) shut();
    });
  }

  function pick(which, d, remember) {
    if (remember) {
      try { window.localStorage.setItem(KEY, which); } catch (err) { /* private mode */ }
    }
    shut();
    go(d, which);
  }

  function ask(d) {
    shell();

    var apps = ANDROID
      ? '<button type="button" class="gs-wa__app" data-wa-go="personal">' + MARK +
          '<b>WhatsApp</b><small>The personal app</small></button>' +
        '<button type="button" class="gs-wa__app gs-wa__app--biz" data-wa-go="business">' + MARK +
          '<b>WhatsApp Business</b><small>The business app</small></button>'
      : '<button type="button" class="gs-wa__app" data-wa-go="personal">' + MARK +
          '<b>Open WhatsApp</b><small>' +
          (IOS ? 'Whichever app iPhone has set' : 'The app on this computer') +
          '</small></button>' +
        '<button type="button" class="gs-wa__app gs-wa__app--web" data-wa-go="web">' + MARK +
          '<b>WhatsApp Web</b><small>In the browser</small></button>';

    var note = IOS
      ? '<p class="gs-wa__note">iPhone decides between the personal and business ' +
        'apps itself and a website cannot override it. To switch, open the app ' +
        'you want and start the chat from there.</p>'
      : '';

    box.innerHTML =
      '<div class="gs-wa__card" role="dialog" aria-modal="true" aria-label="Choose WhatsApp">' +
        '<div class="gs-wa__hd">' +
          '<span>Message +' + d.phone.replace(/^(\d{3})(\d{2})(\d{3})(\d+)$/, '$1 $2 $3 $4') + '</span>' +
          '<button type="button" class="gs-wa__x" data-wa-shut aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="gs-wa__apps">' + apps + '</div>' +
        note +
        (ANDROID
          ? '<button type="button" class="gs-wa__web" data-wa-go="web">Use WhatsApp Web instead</button>'
          : '') +
        '<label class="gs-wa__keep"><input type="checkbox" data-wa-keep>' +
          'Remember this and stop asking</label>' +
      '</div>';

    box.hidden = false;

    var keep = box.querySelector('[data-wa-keep]');
    Array.prototype.forEach.call(box.querySelectorAll('[data-wa-go]'), function (b) {
      b.addEventListener('click', function () {
        pick(b.dataset.waGo, d, !!(keep && keep.checked));
      });
    });

    var first = box.querySelector('[data-wa-go]');
    if (first) first.focus();
  }

  /* ----------------------------------------------------------- the hook */

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;

    var a = e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || a.dataset.gsWaRaw === '1') return;

    var d = parse(a.getAttribute('href') || '');
    if (!d) return;

    e.preventDefault();

    var saved = '';
    try { saved = window.localStorage.getItem(KEY) || ''; } catch (err) { saved = ''; }

    /* A remembered answer skips the sheet - except on a platform where the
       answer was never a real choice, which would make "remembered" a lie. */
    if (saved && (ANDROID || saved === 'web')) { go(d, saved); return; }

    ask(d);
  }, true);

  window.GSWa = {
    ask: ask,
    forget: function () {
      try { window.localStorage.removeItem(KEY); } catch (err) { /* ignore */ }
    }
  };
})();
